import React from "react";
import { BadgeCell } from "./tableCellBadgeRenderer";
import { RealmBadge } from "@/components/shared/quickbooks/RealmBadge";
import { TitleCell } from "./tableCellTitleRenderer";
import {
  DueDateCell,
  EmailAliasesCell,
  PhoneAliasesCell,
  JsonFieldCellRenderer,
  OfferingLineItemsCellRenderer,
  TagsFieldCellRenderer,
  LinkedEntityIdCell,
  DefaultCell,
} from "./tableCellSpecializedFields";
import type { BaseEntity } from "../../types";
import {
  toSpecializedCellProps,
  type CellRendererProps,
} from "./TableCellRouter/cell-renderer-props";
import { badgeDisplayText, resolveCellKind } from "./TableCellRouter/resolve-cell-kind";

export type { CellRendererProps };

/**
 * Determines and renders the appropriate cell component based on field type.
 *
 * The routing DECISION lives in `TableCellRouter/resolve-cell-kind.ts` (pure,
 * and where the precedence rules are documented); this module only maps the
 * resolved kind to a component. Split out under module-size lane L17 —
 * behaviour is pinned by `tests/universal/table-cell-router.test.tsx`.
 */
export function getCellRenderer<TData extends BaseEntity>(
  props: CellRendererProps<TData>
): React.ReactNode {
  const { fieldId, fieldValue, fieldConfig, config } = props;
  const specialized = toSpecializedCellProps(props);

  switch (resolveCellKind(fieldId, fieldConfig, config)) {
    case "badge":
      return (
        <div className="inline-block">
          <BadgeCell
            value={fieldValue}
            fieldId={fieldId}
            fieldConfig={fieldConfig}
            item={props.item}
            isEditing={props.isEditing}
            state={props.state}
            callbacks={props.callbacks}
            auth={props.auth}
            data={props.data}
            inlineEditHandler={props.inlineEditHandler}
            customDisplayText={badgeDisplayText(fieldId, fieldValue)}
          />
        </div>
      );

    case "title":
      return (
        <div className="font-medium text-card-foreground min-w-0">
          <TitleCell
            {...specialized}
            onOpenDetailModal={props.onOpenDetailModal}
            dataRowIds={props.dataRowIds}
            dataIndex={props.dataIndex}
            hooks={props.hooks}
            onEdit={props.onEdit}
            onView={props.onView}
            onDelete={props.onDelete}
          />
        </div>
      );

    case "due-date":
      return (
        <div className="inline-block">
          <DueDateCell {...specialized} />
        </div>
      );

    case "email-aliases":
      return <EmailAliasesCell {...specialized} />;

    case "phone-aliases":
      return <PhoneAliasesCell {...specialized} />;

    case "realm-badge":
      return (
        <div className="inline-block">
          <RealmBadge realmId={typeof fieldValue === "string" ? fieldValue : null} />
        </div>
      );

    case "line-items":
      return <OfferingLineItemsCellRenderer {...specialized} />;

    case "json":
      return <JsonFieldCellRenderer {...specialized} />;

    case "tags":
      return <TagsFieldCellRenderer {...specialized} />;

    case "linked-entity":
      return <LinkedEntityIdCell {...specialized} />;

    default:
      return <DefaultCell {...specialized} />;
  }
}
